/**
 * React Query хук для работы с семьями пациентов
 * Дата: 09.01.2026 - Создан для TODO-029 (Patient Families)
 * Связан с: frontend/src/api/families.ts, frontend/src/pages/PatientFamilies.tsx
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { familiesApi } from '@/api/families';
import { CreateFamilyRequest, UpdateFamilyRequest, CreateFamilyFromDuplicatesRequest } from '@/api/types';
import { useAuth } from './useAuth';
import { useToast } from './useToast';

// Дата: 09.01.2026 - Исправление: showToast заменен на showSuccess/showError

export function useFamilies(tenantId?: string, offset?: number, limit?: number) {
  const { user } = useAuth();
  const effectiveTenantId = tenantId || user?.tenant_id;

  return useQuery({
    queryKey: ['families', effectiveTenantId, offset, limit],
    queryFn: () => familiesApi.list(effectiveTenantId, offset, limit),
    enabled: !!effectiveTenantId,
  });
}

export function useFamily(id: string, tenantId?: string) {
  const { user } = useAuth();
  const effectiveTenantId = tenantId || user?.tenant_id;

  return useQuery({
    queryKey: ['family', id, effectiveTenantId],
    queryFn: () => familiesApi.get(id, effectiveTenantId),
    enabled: !!id && !!effectiveTenantId,
  });
}

export function useDuplicates(tenantId?: string) {
  const { user } = useAuth();
  const effectiveTenantId = tenantId || user?.tenant_id;

  return useQuery({
    queryKey: ['duplicates', effectiveTenantId],
    queryFn: () => familiesApi.findDuplicates(effectiveTenantId),
    enabled: !!effectiveTenantId,
  });
}

export function useCreateFamily() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ data, tenantId }: { data: CreateFamilyRequest; tenantId?: string }) =>
      familiesApi.create(data, tenantId || user?.tenant_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      showSuccess('Семья успешно создана');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при создании семьи');
    },
  });
}

export function useUpdateFamily() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ id, data, tenantId }: { id: string; data: UpdateFamilyRequest; tenantId?: string }) =>
      familiesApi.update(id, data, tenantId || user?.tenant_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      showSuccess('Семья успешно обновлена');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при обновлении семьи');
    },
  });
}

export function useDeleteFamily() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ id, tenantId }: { id: string; tenantId?: string }) =>
      familiesApi.delete(id, tenantId || user?.tenant_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      showSuccess('Семья успешно удалена');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при удалении семьи');
    },
  });
}

export function useAddFamilyMember() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ familyId, patientId, role, tenantId }: { familyId: string; patientId: string; role?: string; tenantId?: string }) =>
      familiesApi.addMember(familyId, patientId, role, tenantId || user?.tenant_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      showSuccess('Член семьи добавлен');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при добавлении члена семьи');
    },
  });
}

export function useRemoveFamilyMember() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ familyId, patientId, tenantId }: { familyId: string; patientId: string; tenantId?: string }) =>
      familiesApi.removeMember(familyId, patientId, tenantId || user?.tenant_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      showSuccess('Член семьи удален');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при удалении члена семьи');
    },
  });
}

export function useSetPrimaryContact() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ familyId, patientId, tenantId }: { familyId: string; patientId: string; tenantId?: string }) =>
      familiesApi.setPrimary(familyId, patientId, tenantId || user?.tenant_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      showSuccess('Основной контакт установлен');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при установке основного контакта');
    },
  });
}

export function useCreateFamilyFromDuplicates() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ data, tenantId }: { data: CreateFamilyFromDuplicatesRequest; tenantId?: string }) =>
      familiesApi.createFromDuplicates(data, tenantId || user?.tenant_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      queryClient.invalidateQueries({ queryKey: ['duplicates'] });
      showSuccess('Семья создана из дубликатов');
    },
    onError: (error: any) => {
      showError(error?.response?.data?.error || 'Ошибка при создании семьи');
    },
  });
}

// Хук для получения семьи по телефону (для ChatWindow)
export function useFamilyByPhone(phone: string, tenantId?: string) {
  const { user } = useAuth();
  const effectiveTenantId = tenantId || user?.tenant_id;

  // Получаем все семьи и фильтруем по телефону на клиенте
  // (Backend не имеет endpoint для получения семьи по телефону, но можно добавить позже)
  const { data } = useFamilies(effectiveTenantId);
  const family = data?.families?.find(f => f.phone_normalized === phone);

  return { family };
}

